export const SPOT_SORT_OPTIONS = Object.freeze({
    DISTANCE: 'distance',
    PRICE: 'price',
    ALPHABETICAL: 'alphabetical',
    FEATURED: 'featured',
    SHUTTLE_TIME: 'shuttleTime',
});

/**
 * Compare function for sorting spots by linear distance from destination.
 * Null distances are treated as 0m due to a bug in the back end with calculating
 * the distance of a spot that is at the destination.
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort|Array Sort}
 * @function distanceComparator
 * @param {object} spotA - first spot for comparison
 * @param {object} spotB - second spot for comparison
 * @returns {number} 1 if spotA is farther than spotB; -1 if spotA is closer than spotB; 0 if equal
 */
export const distanceComparator = (spotA, spotB) => {
    const distanceA = spotA.distance?.linearMeters || 0;
    const distanceB = spotB.distance?.linearMeters || 0;

    return distanceA - distanceB;
};

/**
 * Compare function for sorting spots by advertised price.
 * A spot may not have a selected rate or advertised price
 * if it is unavailable (sold out, blacked out, etc.).
 * In this case we treat it as having the highest possible price
 * it gets sorted to the end.
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort|Array Sort}
 * @function advertisedPriceComparator
 * @param {object} spotA - first spot for comparison
 * @param {object} spotB - second spot for comparison
 * @returns {number} 1 if spotA is more expensive than spotB; -1 if spotA is cheaper than spotB; 0 if equal
 */
export const advertisedPriceComparator = (spotA, spotB) => {
    const priceA =
        spotA.selectedRate?.advertisedPrice?.value || Number.MAX_SAFE_INTEGER;
    const priceB =
        spotB.selectedRate?.advertisedPrice?.value || Number.MAX_SAFE_INTEGER;

    return priceA - priceB;
};

/**
 * Compare function for sorting spots alphabetically by title
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort|Array Sort}
 * @function spotTitleComparator
 * @param {object} spotA - first spot for comparison
 * @param {object} spotB - second spot for comparison
 * @returns {number} 1 if spotA is alphabetically after spotB; -1 if spotA is before spotB; 0 if equal
 */
export const spotTitleComparator = (spotA, spotB) => {
    const titleA = spotA.title.toUpperCase(); // ignore upper and lowercase
    const titleB = spotB.title.toUpperCase(); // ignore upper and lowercase

    if (titleA < titleB) {
        return -1;
    }
    if (titleA > titleB) {
        return 1;
    }

    return 0;
};

/**
 * Given an airport tag (generated in the monolith), return the order in which this
 * should be featured. Most Popular should be featured first, followed by Lowest Price,
 * Highest Rated. Any spot without one of these tags comes last.
 *
 * @function getAirportTagOrder
 * @param {string} tag - an optional string describing spot superlatives
 * @returns {number} indicating featured order
 */
const getAirportTagOrder = tag => {
    switch (tag) {
        case 'Most Popular':
            return 0;
        case 'Lowest Price':
            return 1;
        case 'Highest Rated':
            return 2;
        default:
            return 3;
    }
};

/**
 * Compare function for sorting spots by tag, then advertised price as a tiebreaker
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort|Array Sort}
 * @function airportFeaturedComparator
 * @param {object} spotA - first spot for comparison
 * @param {object} spotB - second spot for comparison
 * @returns {number} 1 if spotA is featured after spotB; -1 if spotA is featured before spotB; 0 if equal
 */
export const airportFeaturedComparator = (spotA, spotB) => {
    const tagOrderA = getAirportTagOrder(spotA.tag);
    const tagOrderB = getAirportTagOrder(spotB.tag);

    if (tagOrderA !== tagOrderB) {
        return tagOrderA - tagOrderB;
    } else {
        return advertisedPriceComparator(spotA, spotB);
    }
};

/**
 * Compare function for sorting spots by transportation duration.
 * All airport spots will have some form of transportation associated with it,
 * however only airport spots with scheduled or on-demand shuttle service
 * will have duration information available.
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort|Array Sort}
 * @function airportTransportationComparator
 * @param {object} spotA - first spot for comparison
 * @param {object} spotB - second spot for comparison
 * @returns {number} 1 if spotA is featured after spotB; -1 if spotA is featured before spotB; 0 if equal
 */
export const airportTransportationComparator = (spotA, spotB) => {
    const shuttleDurationA =
        spotA.transportation?.schedule?.duration || Number.MAX_SAFE_INTEGER;
    const shuttleDurationB =
        spotB.transportation?.schedule?.duration || Number.MAX_SAFE_INTEGER;

    return shuttleDurationA - shuttleDurationB;
};
